(function() {
	'use strict';

	angular
		.module('snap-calendar')
		.factory('SnapCalendarUtilities', SnapCalendarUtilities);

	/**
	 * Our constructor
	 * @constructor
	 * @param {Service} $filter $filter
	 * @param {Service} AccessoCalendarEnums Enums
	 * @param {Service} AppData AppData
	 * @param {Service} DynamicCapacityPricingService DynamicCapacityPricingService
	 * @param {Service} Enums Enums
	 * @param {Service} Package Package
	 * @param {Service} Utilities Utilities
	 * @param {Service} UtilsPricing UtilsPricing
	 * @param {Service} UtilsValidation UtilsValidation
	 * @returns {self} Our instance
	 * @ngInject
	 */
	function SnapCalendarUtilities(
		$filter,
		AccessoCalendarEnums,
		AppData,
		DynamicCapacityPricingService,
		Enums,
		Package,
		Utilities,
		UtilsPricing,
		UtilsValidation
	) {
		var validString = UtilsValidation.validString,
			validArray = UtilsValidation.validArray,
			wrapObjectToArray = Utilities.wrapObjectToArray,
			checkProp = Utilities.checkProp,
			forceInt = Utilities.forceInt;

		var service = {
			formatTime: formatTime,
			getCalendarDisplayType: getCalendarDisplayType,
			getCustomerTypesForView: getCustomerTypesForView,
			getCustomerTypesFromHash: getCustomerTypesFromHash,
			getDateFromStateParams: getDateFromStateParams,
			getFirstAvailableDate: getFirstAvailableDate,
			getHashFromCustomerTypes: getHashFromCustomerTypes,
			getLowestPriceForEachPackage: getLowestPriceForEachPackage,
			getMatrixWizardChildPackages: getMatrixWizardChildPackages,
			getMonthByDate: getMonthByDate,
			isMatrixWizardPackageChild: isMatrixWizardPackageChild,
			isMatrixWizardPackageParent: isMatrixWizardPackageParent,
			isNotMatrixWizardPackageChild: isNotMatrixWizardPackageChild,
			packageHasMainCT: packageHasMainCT,
			setUnavailableDatesByCustomerQuantity: setUnavailableDatesByCustomerQuantity
		};

		return service;

		/**
		 * Returns whether or not a package's main CT id matches a CT id in a given array of CTs
		 * @public
		 * @param {String} pkgID The package id for determining the main CT
		 * @param {Array} activeCTs Array of CTs given
		 * @returns {Boolean} Whether the package's main ct exists in the active CT's selected by the user
		 */
		function packageHasMainCT(pkgID, activeCTs) {
			var mainCT = Package.getMainCustomerType(pkgID),
				hasMainCT = activeCTs.find(function findMatchingCtId(ct) {
					return ct.id === mainCT.id;
				});

			return hasMainCT;
		}

		/**
		 * Formats time to match backend response
		 * @param {Date} date The date
		 * @returns {String} the formatted time
		 */
		function formatTime(date) {
			if (!(date instanceof Date)) {
				return '';
			}

			return $filter('date')(date, 'h:mm') + ' ' + (date.getHours() >= 12 ? 'PM' : 'AM');
		}

		/**
		 * Returns the type of calendar to display for the package
		 * @param {String} packageId The package ID
		 * @returns {String} week or month
		 */
		function getCalendarDisplayType(packageId) {
			var pkg,
				displayType;

			if (!Number.isNaN(packageId)) {
				return AccessoCalendarEnums.displayType.week;
			}

			pkg = Package.getPackageByID(packageId);
			displayType = checkProp('pkg.CHARACS.snap_calendar_view', pkg, 'returnValue');

			if ([AccessoCalendarEnums.displayType.week, AccessoCalendarEnums.displayType.month].includes(displayType)) {
				return displayType;
			}

			return AccessoCalendarEnums.displayType.week;
		}

		/**
		 * Returns the customer types based on a hash.
		 * @param {String} hash The url hash in the form of `{CUSTOMER_TYPE_ID}-{QTY}_{CUSTOMER_TYPE_ID}-{QTY}`
		 * @example This hash: 31-2_433-5 would become:
		 * 		{
		 * 			31: 2,
		 * 			433: 5,
		 * 		}
		 * @returns {Object} The customer type map
		 */
		function getCustomerTypesFromHash(hash) {
			var customerTypes = {};

			if (!validString(hash)) {
				return customerTypes;
			}

			return hash.split('_').reduce(function reduceHashToCustomerTypes(acc, part) {
				var parts = part.split('-'),
					customerTypeId,
					quantity;

				if (parts.length === 2 && parts.every(validString)) {
					customerTypeId = forceInt(parts[0]);
					quantity = forceInt(parts[1]);

					if (Number.isNaN(customerTypeId) || Number.isNaN(quantity)) {
						return acc;
					}

					acc[customerTypeId] = quantity;
				}

				return acc;
			}, customerTypes);
		}

		/**
		 * Returns the first available date
		 * @param {String} keywordCalendarResponse The response
		 * @returns {Date|null} The first available date
		 */
		function getFirstAvailableDate(keywordCalendarResponse) {
			var status = checkProp('data.status', keywordCalendarResponse, 'returnValue'),
				dates = checkProp('data.DATES.D', keywordCalendarResponse, 'returnValue'),
				dateStringToObject = Utilities.dateStringToObject;

			// If the call failed or we dont have any dates return null
			if (status !== Enums.status.OK || !validArray(dates)) {
				return null;
			}

			// Sort them so that they are in chronological order
			dates.sort(function sortFoundDates(a, b) {
				var aDate = dateStringToObject(a.date);
				var bDate = dateStringToObject(b.date);

				return aDate.getTime() - bDate.getTime();
			});

			// Grab the first date and return a Date object
			return dateStringToObject(dates[0].date);
		}

		/**
		 * Returns a hash from the customer type Map
		 * @param {Object} customerTypes The customer types and their quantity
		 * @returns {String} a hashed representation of the selected customer types
		 */
		function getHashFromCustomerTypes(customerTypes) {
			return Object
				.keys(customerTypes)
				.reduce(function reduceCustomerTypesWithQuantities(acc, customerTypeId) {
					var qty = forceInt(customerTypes[customerTypeId]);
					if (Number.isNaN(qty) || qty <= 0) {
						return acc;
					}

					acc.push(customerTypeId + '-' + qty);

					return acc;
				}, [])
				.join('_');
		}

		/**
		 * Returns the month to use in the backend request based on a string date
		 * @param {String} date The date string in yyyy-mm-dd format
		 * @returns {Number|String} the month to use in the backend request
		 */
		function getMonthByDate(date) {
			var newDate,
				today;

			if (date instanceof Date) {
				return date;
			}

			newDate = Utilities.dateStringToObject(date || '');
			today = new Date();

			if (!(newDate instanceof Date)) {
				return null;
			}

			today.setHours(12, 0, 0, 0);

			if (today > newDate) {
				if (today.getFullYear() > newDate.getFullYear()) {
					return null;
				} else if (today.getMonth() > newDate.getMonth()) {
					return null;
				}
			}

			return newDate;
		}

		/**
		 * Returns the child packages of the parent matrix wizard package
		 * @param {String} parentPackageId The package ID
		 * @param {String} keyword The keyword we're on
		 * @returns {Object[]} the child packages
		 */
		function getMatrixWizardChildPackages(parentPackageId, keyword) {
			return Package.getPackagesByKeyword(keyword)
				.filter(function reducePackages(pkg) {
					return checkProp('pkg.CHARACS.parentPackageID', pkg, 'returnValue') === parentPackageId;
				});
		}

		/**
		 * Check if a package uses matrix wizard package setup and is a child package of the parent
		 * @param {Object} pkg The package to check
		 * @returns {Boolean} If its a matrix wizard child package
		 */
		function isMatrixWizardPackageChild(pkg) {
			return checkProp('pkg.CHARACS.parentPackageID', pkg, 'validString');
		}

		/**
		 * Check if a package is NOT a matrix wizard child package
		 * @param {Object} pkg The package to check
		 * @returns {Boolean} If its a matrix wizard child package
		 */
		function isNotMatrixWizardPackageChild(pkg) {
			return !service.isMatrixWizardPackageChild(pkg);
		}

		/**
		 * Find out if the package is a matrix wizard package parent
		 * @param {Object} pkg The package to check
		 * @returns {Boolean} if it's the parent or not
		 */
		function isMatrixWizardPackageParent(pkg) {
			return checkProp('pkg.CHARACS.block_purchase', pkg, 'returnValue') === '1';
		}

		/**
		 * Returns the customer types available
		 * @private
		 * @param {Object} pkg A package
		 * @param {Object} customerTypesFromRoute The customer types preselected
		 * @returns {Object|null} The customer type model
		 */
		function getCustomerTypesForView(pkg, customerTypesFromRoute) {
			if (!angular.isObject(pkg)) {
				return null;
			}

			return pkg.CT.map(function mapCustomerTypeModel(ct) {
				var qty = customerTypesFromRoute[ct.id] || 0;

				return {
					id: ct.id,
					name: ct.name,
					qty: qty
				}
			})
		}

		/**
		 * Returns the lowest price for all packages based on a GetKeywordCalendar response
		 * @param {Object} data The GetKeywordCalendar response
		 * @returns {Object} A Map of package->price
		 */
		function getLowestPriceForEachPackage(data) {
			// using normalized model data
			var lowestPrices = {},
				useVat = AppData.defaults.use_vat,
				status = checkProp('data.status', data, 'returnValue'),
				dates = checkProp('data.dates', data, 'returnValue');

			if (status !== Enums.status.OK || !angular.isObject(dates)) {
				return lowestPrices;
			}

			return wrapObjectToArray(dates, true)
				.reduce(function reduceToLowestPriceForEachPackage(acc, date) {
					var pkgs = wrapObjectToArray(checkProp('date.packages', date, 'returnValue'), true);

					pkgs.forEach(function forEachPkgCheckLowestPrice(pkg) {
						var ct = Package.getMainCustomerType(pkg.id),
							dynamicPricing,
							lowestPrice,
							price;

						pkg.customer = wrapObjectToArray(pkg.customer, true);
						lowestPrice = pkg.customer.find(function findMainCustomerTypePrice(price) {
							return ct.id == price.id;// my model is ints and raw data is string
						});

						if (!lowestPrice) {
							lowestPrice = pkg.customer[0];
						}

						dynamicPricing = DynamicCapacityPricingService.getDynamicPricingValues(lowestPrice, pkg.id);
						if (dynamicPricing) {
							price = useVat ? DynamicCapacityPricingService.getVatPrice(dynamicPricing) : dynamicPricing.price;
						} else {
							price = UtilsPricing.getPrice(lowestPrice.pricing.retail, ct.tax_amount, ct.fee_total);
						}

						if (!acc[pkg.id] || acc[pkg.id].price > price) {
							acc[pkg.id] = {
								customerTypeId: lowestPrice.id,
								isDynamicPricing: !!dynamicPricing,
								price: price
							};
						}

						return acc;
					});

					return acc;
				}, lowestPrices);
		}

		/**
		 * Returns the date from state params
		 * @param {Object} $stateParams The state params object
		 * @returns {Date|null} The a date object
		 */
		function getDateFromStateParams($stateParams) {
			var today = new Date(),
				date = Utilities.dateStringToObject($stateParams.date, '12:00 PM');

			today.setHours(12, 0, 0, 0);

			if (today > date) {
				return null;
			}
			return date;
		}

		/**
		 * Set unavailable dates
		 * @param {Object[]} items The array response from AccessoCalendarDataService.normalizeDataFromResponse()
		 * @param {Number} totalQuantity The total number of guests
		 * @returns {Object[]} The modified response
		 */
		function setUnavailableDatesByCustomerQuantity(items, totalQuantity) {
			items.forEach(function setUnavailableForCustomerTypeAvailability(item) {
				var dataMap = item.dataMap || {},
					noDataMap = !checkProp('item.dataMap', item, 'validObject'),
					noCapacity = !dataMap.capacity && dataMap.available !== -1,
					doesntMeetCustomerTypeAvailability = dataMap.available !== -1 && dataMap.available < totalQuantity;

				item.unavailable = noDataMap || noCapacity || doesntMeetCustomerTypeAvailability;
				item.isSoldOut = dataMap.isSoldOut;
			});

			return items;
		}
	}
}());